import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Wrench, Shield, PhoneCall, Star, Check, ChevronRight, X, PenLine } from "lucide-react";
import api, { MEDIA } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useSettings } from "../contexts/SettingsContext";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function Home() {
    const [banners, setBanners] = React.useState([]);
    const [products, setProducts] = React.useState([]);
    const [packages, setPackages] = React.useState([]);
    const [reviews, setReviews] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [activeBanner, setActiveBanner] = React.useState(0);
    const { settings } = useSettings();
    const { user } = useAuth();

    // Review modal state
    const [reviewModalOpen, setReviewModalOpen] = React.useState(false);
    const [reviewableProducts, setReviewableProducts] = React.useState(null); // null = not loaded
    const [reviewForm, setReviewForm] = React.useState({ product_id: "", rating: 5, author_name: "", title: "", comment: "" });
    const [reviewSubmitting, setReviewSubmitting] = React.useState(false);

    const [newArrivals, setNewArrivals] = React.useState([]);
    const [onSaleProducts, setOnSaleProducts] = React.useState([]);

    React.useEffect(() => {
        api.get("/banners").then(r => setBanners(r.data || []));
        api.get("/products?featured=true&limit=8").then(r => setProducts(r.data?.items || []));
        api.get("/products?sort=newest&limit=4").then(r => setNewArrivals(r.data?.items || []));
        api.get("/products?on_sale=true&limit=4").then(r => setOnSaleProducts(r.data?.items || []));
        api.get("/packages?featured=true").then(r => setPackages(r.data || []));
        api.get("/reviews").then(r => setReviews((r.data || []).slice(0, 6)));
        api.get("/categories").then(r => setCategories(r.data || []));
    }, []);

    React.useEffect(() => {
        if (banners.length > 1) {
            const t = setInterval(() => setActiveBanner(b => (b + 1) % banners.length), 6000);
            return () => clearInterval(t);
        }
    }, [banners.length]);

    const wa = (settings.whatsapp_number || "").replace(/\D/g, "");

    const openReviewModal = async () => {
        if (!user) { toast.info("Autentifică-te pentru a lăsa o recenzie"); return; }
        if (!reviewableProducts) {
            try {
                const r = await api.get("/orders/my/reviewable-products");
                setReviewableProducts(r.data || []);
                const first = (r.data || []).find(p => !p.already_reviewed);
                setReviewForm(f => ({ ...f, product_id: first?.product_id || "", author_name: user.name || "" }));
            } catch { setReviewableProducts([]); }
        }
        setReviewModalOpen(true);
    };

    const submitReview = async (e) => {
        e.preventDefault();
        if (!reviewForm.product_id) return toast.error("Selectează un produs");
        setReviewSubmitting(true);
        try {
            await api.post("/reviews", reviewForm);
            toast.success("Recenzie trimisă! Va fi publicată după aprobare.");
            setReviewModalOpen(false);
            setReviewForm(f => ({ ...f, product_id: "", rating: 5, title: "", comment: "" }));
            // Invalidate cache so next open re-fetches
            setReviewableProducts(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Eroare la trimitere");
        } finally { setReviewSubmitting(false); }
    };

    return (
        <div data-testid="home-page">
            {/* Hero Carousel */}
            <section className="relative bg-foreground text-background overflow-hidden rounded-b-[1.5rem] sm:rounded-b-[2.5rem]">
                {banners.length > 0 && (
                    <div className="relative h-[320px] sm:h-[420px] md:h-[520px] lg:h-[600px]">
                        {banners.map((b, i) => (
                            <div key={b.banner_id}
                                 className={`absolute inset-0 transition-opacity duration-700 ${i === activeBanner ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                                <img src={MEDIA(b.image)} alt={b.title} className="absolute inset-0 w-full h-full object-cover opacity-50" />
                                <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-foreground/95 via-foreground/60 to-foreground/30 sm:via-foreground/40 sm:to-transparent" />
                                <div className="container-x relative h-full flex flex-col justify-end pb-5 sm:pb-12 md:pb-20 max-w-3xl">
                                    <h1 className="font-heading text-xl sm:text-4xl md:text-5xl lg:text-7xl font-semibold tracking-tight leading-tight sm:leading-[0.95] mb-2 sm:mb-5" data-testid={`banner-title-${i}`}>{b.title}</h1>
                                    <p className="hidden sm:block text-sm sm:text-lg text-background/80 max-w-xl mb-4 sm:mb-8">{b.subtitle}</p>
                                    <div className="flex flex-wrap gap-2 sm:gap-3">
                                        <Link to={b.link || "/magazin"} className="btn-primary text-xs sm:text-base py-2 sm:py-3 px-4 sm:px-6" data-testid={`banner-cta-${i}`}>
                                            {b.cta_text || "Vezi oferta"} <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
                                        </Link>
                                        {b.show_install_cta && (
                                            <Link to={b.install_cta_link || "/programare-instalare"}
                                                  className="hidden sm:inline-flex bg-background/10 text-background backdrop-blur border border-background/30 hover:bg-background hover:text-foreground transition-colors px-6 py-3 rounded-xl font-medium"
                                                  data-testid={`banner-install-${i}`}>
                                                {b.install_cta_text || "Programează instalare"}
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {banners.length > 1 && (
                            <div className="absolute bottom-4 sm:bottom-8 right-4 sm:right-8 flex gap-2 z-10">
                                {banners.map((_, i) => (
                                    <button key={i} onClick={() => setActiveBanner(i)}
                                            className={`h-1 sm:h-1.5 rounded-full transition-all ${i === activeBanner ? "w-8 sm:w-12 bg-primary" : "w-4 sm:w-6 bg-background/40"}`}
                                            data-testid={`banner-dot-${i}`} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/* Trust badges */}
            <section className="container-x py-5 sm:py-10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                    {[
                        { icon: Truck, label: "Livrare rapidă", sub: "24-48h oriunde în țară", color: "bg-blue-500/10 text-blue-600" },
                        { icon: Wrench, label: "Instalare pro", sub: "Echipă certificată", color: "bg-amber-500/10 text-amber-600" },
                        { icon: Shield, label: "Garanție", sub: "Minimum 24 luni", color: "bg-emerald-500/10 text-emerald-600" },
                        { icon: PhoneCall, label: "Suport 24/7", sub: "Consultanță gratuită", color: "bg-primary/10 text-primary" },
                    ].map((f, i) => (
                        <div key={i} className="bg-card border border-border/60 rounded-2xl p-3 sm:p-5 flex items-center gap-2 sm:gap-4 hover:border-primary/30 hover:shadow-md transition-all duration-300 animate-fade-up" style={{ animationDelay: `${i * 0.07}s` }} data-testid={`trust-badge-${i}`}>
                            <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-xl ${f.color} flex items-center justify-center flex-shrink-0`}>
                                <f.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                            </div>
                            <div className="min-w-0">
                                <div className="font-semibold text-xs sm:text-sm leading-tight">{f.label}</div>
                                <div className="text-[10px] sm:text-xs text-muted-foreground leading-tight mt-0.5">{f.sub}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Categories */}
            <section className="py-8 sm:py-14 container-x">
                <div className="flex items-center justify-between mb-4 sm:mb-8">
                    <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-semibold tracking-tight">Găsește soluția potrivită</h2>
                    <Link to="/magazin" className="flex items-center gap-1 text-xs sm:text-sm text-primary hover:underline whitespace-nowrap ml-4">
                        <span className="hidden sm:inline">Toate categoriile</span>
                        <span className="sm:hidden">Toate</span>
                        <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Link>
                </div>

                {/* Mobile: horizontal scroll strip */}
                {categories.length > 0 && (
                    <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory md:hidden no-scrollbar -mx-5 px-5">
                        {categories.map(c => (
                            <Link key={c.category_id} to={`/magazin/${c.slug}`}
                                  className="flex-shrink-0 snap-start flex flex-col items-center gap-1.5 w-[72px]"
                                  data-testid={`home-cat-mobile-${c.slug}`}>
                                <div className="w-16 h-16 rounded-2xl bg-muted overflow-hidden border border-border flex-shrink-0">
                                    {c.image
                                        ? <img src={MEDIA(c.image)} alt={c.name} className="w-full h-full object-cover" />
                                        : <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5" />
                                    }
                                </div>
                                <span className="text-[10px] text-center leading-tight font-medium line-clamp-2 w-full">{c.name}</span>
                            </Link>
                        ))}
                        <Link to="/magazin"
                              className="flex-shrink-0 snap-start flex flex-col items-center gap-1.5 w-[72px]">
                            <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                                <ChevronRight className="h-6 w-6 text-primary" />
                            </div>
                            <span className="text-[10px] text-center leading-tight font-medium text-primary">Toate</span>
                        </Link>
                    </div>
                )}

                {/* Desktop: Bento grid */}
                <div className="hidden md:grid grid-cols-12 gap-4">
                    {categories.slice(0, 6).map((c, i) => {
                        const layouts = ["col-span-8 row-span-2", "col-span-4 aspect-square", "col-span-4 aspect-square", "col-span-5 aspect-[4/3]", "col-span-3 aspect-square", "col-span-4 aspect-square"];
                        return (
                            <Link key={c.category_id} to={`/magazin/${c.slug}`} className={`relative group overflow-hidden rounded-2xl bg-muted ${layouts[i % layouts.length]}`} data-testid={`home-category-${c.slug}`}>
                                {c.image && <img src={MEDIA(c.image)} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />}
                                <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/30 to-transparent" />
                                <div className="absolute bottom-0 left-0 p-5 md:p-7 text-background">
                                    <div className="font-heading text-2xl md:text-3xl font-semibold">{c.name}</div>
                                    <div className="text-sm text-background/80 mt-1 flex items-center gap-2">Explorează <ArrowRight className="h-3 w-3" /></div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* Popular Products */}
            <section className="py-8 sm:py-14 bg-muted/50 rounded-2xl sm:rounded-3xl my-4 sm:my-8 mx-3 sm:mx-5 md:mx-10">
                <div className="max-w-7xl mx-auto px-3 sm:px-5 md:px-10">
                    <div className="flex items-center justify-between mb-4 sm:mb-8">
                        <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-semibold tracking-tight">Produse populare</h2>
                        <Link to="/magazin" className="flex items-center gap-1 text-xs sm:text-sm text-primary hover:underline whitespace-nowrap ml-4">
                            <span className="hidden sm:inline">Vezi toate</span>
                            <span className="sm:hidden">Toate</span>
                            <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                        {products.map(p => <ProductCard key={p.product_id} product={p} />)}
                    </div>
                </div>
            </section>

            {/* New arrivals */}
            {newArrivals.length > 0 && (
                <section className="py-8 sm:py-14 container-x">
                    <div className="flex items-center justify-between mb-4 sm:mb-8">
                        <div>
                            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-semibold tracking-tight">Nou în stoc</h2>
                            <p className="text-xs sm:text-sm text-muted-foreground mt-1">Produse recent adăugate</p>
                        </div>
                        <Link to="/magazin?sort=newest" className="flex items-center gap-1 text-xs sm:text-sm text-primary hover:underline whitespace-nowrap ml-4">
                            <span className="hidden sm:inline">Vezi toate</span><span className="sm:hidden">Toate</span>
                            <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                        {newArrivals.map(p => <ProductCard key={p.product_id} product={p} />)}
                    </div>
                </section>
            )}

            {/* On sale */}
            {onSaleProducts.length > 0 && (
                <section className="py-8 sm:py-14 bg-primary/5 rounded-2xl sm:rounded-3xl my-4 sm:my-8 mx-3 sm:mx-5 md:mx-10">
                    <div className="max-w-7xl mx-auto px-3 sm:px-5 md:px-10">
                        <div className="flex items-center justify-between mb-4 sm:mb-8">
                            <div>
                                <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-semibold tracking-tight">Oferte speciale</h2>
                                <p className="text-xs sm:text-sm text-muted-foreground mt-1">Prețuri reduse, stoc limitat</p>
                            </div>
                            <Link to="/magazin?on_sale=true" className="flex items-center gap-1 text-xs sm:text-sm text-primary hover:underline whitespace-nowrap ml-4">
                                <span className="hidden sm:inline">Toate ofertele</span><span className="sm:hidden">Toate</span>
                                <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                            {onSaleProducts.map(p => <ProductCard key={p.product_id} product={p} />)}
                        </div>
                    </div>
                </section>
            )}

            {/* Why us */}
            <section className="py-8 sm:py-14 container-x">
                <div className="text-center mb-6 sm:mb-10">
                    <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-semibold tracking-tight">De ce să alegi ccashop?</h2>
                    <p className="text-sm text-muted-foreground mt-2 max-w-xl mx-auto">Oferim soluții complete de securitate — de la consultanță la instalare și suport post-vânzare.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {[
                        { icon: Shield, title: "Produse certificate", desc: "Toate produsele sunt certificate CE și conforme cu standardele europene de securitate." },
                        { icon: Wrench, title: "Instalare profesională", desc: "Echipă de instalatori autorizați cu experiență în sisteme de securitate rezidențiale și comerciale." },
                        { icon: Truck, title: "Livrare rapidă", desc: "Livrăm în toată România în 24-48h prin Fan Courier și Sameday Courier." },
                        { icon: PhoneCall, title: "Suport tehnic dedicat", desc: "Asistență tehnică disponibilă non-stop pentru clienții noștri, inclusiv intervenții de urgență." },
                        { icon: Star, title: "Garanție extinsă", desc: "Minim 24 luni garanție pe toate produsele și 12 luni pentru manopera de instalare." },
                        { icon: Check, title: "Prețuri transparente", desc: "Fără costuri ascunse. Ofertă detaliată înainte de orice lucrare. Plată în rate disponibilă." },
                    ].map((f, i) => (
                        <div key={i} className="card-soft p-5 sm:p-6 flex gap-4 items-start hover:border-primary/30 hover:shadow-md transition-all duration-300">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                                <f.icon className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="font-semibold text-sm sm:text-base mb-1">{f.title}</div>
                                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Packages */}
            {packages.length > 0 && (
                <section className="py-8 sm:py-14 container-x">
                    <div className="flex items-center justify-between mb-4 sm:mb-8">
                        <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-semibold tracking-tight">Pachete cu instalare</h2>
                        <Link to="/pachete" className="flex items-center gap-1 text-xs sm:text-sm text-primary hover:underline whitespace-nowrap ml-4">
                            <span className="hidden sm:inline">Toate pachetele</span>
                            <span className="sm:hidden">Toate</span>
                            <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        {packages.map(p => (
                            <Link key={p.package_id} to={`/pachete/${p.slug}`} className="card-soft overflow-hidden group" data-testid={`package-card-${p.slug}`}>
                                <div className="aspect-[4/3] bg-muted overflow-hidden">
                                    {p.image && <img src={MEDIA(p.image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                                </div>
                                <div className="p-4 sm:p-6">
                                    {p.includes_installation && <div className="chip-primary mb-2 sm:mb-3 text-[10px] sm:text-xs"><Check className="h-2.5 w-2.5 sm:h-3 sm:w-3" /> Instalare inclusă</div>}
                                    <div className="font-heading text-base sm:text-xl font-semibold mb-1 sm:mb-2">{p.name}</div>
                                    <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 mb-3 sm:mb-4">{p.description}</p>
                                    <div className="flex items-end justify-between">
                                        <div>
                                            {p.sale_price && p.sale_price < p.price && <div className="text-xs text-muted-foreground line-through">{p.price.toFixed(2)} RON</div>}
                                            <div className="font-heading text-lg sm:text-2xl font-bold text-primary">{(p.sale_price || p.price).toFixed(2)} <span className="text-[10px] sm:text-xs text-foreground">RON</span></div>
                                        </div>
                                        <span className="text-xs sm:text-sm text-primary">Detalii →</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Reviews */}
            <section className="py-8 sm:py-14 bg-foreground text-background mx-3 sm:mx-5 md:mx-10 rounded-2xl sm:rounded-3xl">
                <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-10">
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5 sm:mb-8">
                        <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-semibold tracking-tight">Ce spun clienții</h2>
                        <button
                            onClick={openReviewModal}
                            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-semibold hover:brightness-110 transition-all flex-shrink-0"
                            data-testid="home-add-review-btn"
                        >
                            <PenLine className="h-4 w-4" /> Lasă o recenzie
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-5">
                        {reviews.map((r, i) => (
                            <div key={r.review_id} className="bg-background/5 backdrop-blur border border-background/10 rounded-2xl p-4 sm:p-6" data-testid={`review-${i}`}>
                                <div className="flex gap-1 text-primary mb-2 sm:mb-3">{[...Array(r.rating)].map((_, j) => <Star key={j} className="h-3 w-3 sm:h-4 sm:w-4 fill-current" />)}</div>
                                <p className="text-sm sm:text-base text-background/90 mb-3 sm:mb-4 italic line-clamp-4">"{r.comment}"</p>
                                <div className="text-xs sm:text-sm text-background/60">— {r.author_name}</div>
                            </div>
                        ))}
                        {reviews.length === 0 && (
                            <div className="md:col-span-3 py-8 text-center text-background/50 text-sm">
                                Fii primul care lasă o recenzie!
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Review modal */}
            {reviewModalOpen && (
                <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={() => setReviewModalOpen(false)}>
                    <div className="bg-card rounded-t-2xl sm:rounded-2xl max-w-lg w-full max-h-[88vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <div>
                                <div className="overline mb-1">Recenzie client</div>
                                <h3 className="font-heading text-xl">Lasă o recenzie</h3>
                            </div>
                            <button onClick={() => setReviewModalOpen(false)} className="btn-ghost"><X className="h-5 w-5" /></button>
                        </div>
                        {reviewableProducts !== null && reviewableProducts.filter(p => !p.already_reviewed).length === 0 ? (
                            <div className="p-8 text-center text-muted-foreground">
                                {reviewableProducts.length === 0
                                    ? "Poți lăsa o recenzie după ce plasezi o comandă."
                                    : "Ai lăsat deja o recenzie pentru toate produsele comandate."}
                            </div>
                        ) : (
                            <form onSubmit={submitReview} className="p-5 space-y-4">
                                {/* Product selector */}
                                <div>
                                    <label className="text-xs text-muted-foreground block mb-1">Produs *</label>
                                    {reviewableProducts === null ? (
                                        <div className="skeleton h-10 rounded-lg" />
                                    ) : (
                                        <select
                                            required
                                            value={reviewForm.product_id}
                                            onChange={e => setReviewForm(f => ({ ...f, product_id: e.target.value }))}
                                            className="input-soft"
                                            data-testid="review-product-select"
                                        >
                                            <option value="">Alege produsul...</option>
                                            {reviewableProducts.filter(p => !p.already_reviewed).map(p => (
                                                <option key={p.product_id} value={p.product_id}>{p.name}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                                {/* Stars */}
                                <div>
                                    <label className="text-xs text-muted-foreground block mb-1">Calificativ *</label>
                                    <div className="flex gap-1">
                                        {[1, 2, 3, 4, 5].map(r => (
                                            <button type="button" key={r} onClick={() => setReviewForm(f => ({ ...f, rating: r }))}>
                                                <Star className={`h-7 w-7 transition-colors ${r <= reviewForm.rating ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <input required placeholder="Numele tău *" value={reviewForm.author_name} onChange={e => setReviewForm(f => ({ ...f, author_name: e.target.value }))} className="input-soft" data-testid="review-author" />
                                <input placeholder="Titlu (opțional)" value={reviewForm.title} onChange={e => setReviewForm(f => ({ ...f, title: e.target.value }))} className="input-soft" />
                                <textarea required rows="4" placeholder="Comentariu *" value={reviewForm.comment} onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))} className="input-soft" data-testid="review-comment" />
                                <button type="submit" disabled={reviewSubmitting} className="btn-primary w-full" data-testid="review-submit">
                                    {reviewSubmitting ? "Se trimite..." : "Trimite recenzia"}
                                </button>
                                <p className="text-xs text-muted-foreground text-center">Recenzia va fi publicată după aprobare.</p>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* Final CTA */}
            <section className="py-8 sm:py-14 container-x">
                <div className="card-soft p-6 sm:p-10 md:p-14 relative overflow-hidden">
                    <div className="relative z-10 max-w-2xl">
                        <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-semibold tracking-tight mb-3 sm:mb-4">Ai nevoie de o ofertă?</h2>
                        <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">Specialiștii noștri te pot consilia gratuit pentru alegerea sistemului potrivit.</p>
                        <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
                            {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium text-sm sm:text-base hover:brightness-105 transition-all" data-testid="home-cta-whatsapp">Scrie pe WhatsApp</a>}
                            <Link to="/contact" className="btn-outline text-sm py-2.5 sm:py-3">Cere ofertă</Link>
                            {settings.contact_phone && <a href={`tel:${settings.contact_phone}`} className="text-primary font-medium text-sm hover:underline">{settings.contact_phone}</a>}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
